/**
 * DESIGN SYSTEM STEP 3 — text holders (owner, 2026-10-13).
 *
 * A holder is a text element with `holder: <role>` placed by a model. Its words
 * come from the order or a person — never invented. The two print rules, shared
 * by the editor, the preview and the PDF:
 *   · an EMPTY holder prints nothing;
 *   · a label shape tied to a holder (`labelFor`) prints only when that holder
 *     has words (« blank labels only exist with a text holder »).
 * The editor still shows both, faded, with the role's name, so a person can fill them.
 */
// Design system v2 §4 (2026-10-14): « Petit mot » became two roles —
// `mot_photo` (Mot sous photo: small script, ≤ 12 characters, under a polaroid)
// and `mot_manuscrit` (Mot manuscrit: big script line, ≤ 30). `petit_mot` stays
// readable for pages built before v2.
export type HolderRole = 'titre' | 'sous_titre' | 'section' | 'lieu_date' | 'legende' | 'mot_photo' | 'mot_manuscrit'
  | 'citation' | 'compteur' | 'petit_mot';

export const HOLDER_HINT: Record<HolderRole, string> = {
  titre: 'Titre', sous_titre: 'Sous-titre', section: 'Section', lieu_date: 'Lieu · date',
  legende: 'Légende', mot_photo: 'Mot sous photo', mot_manuscrit: 'Mot manuscrit',
  citation: 'Citation', compteur: 'Compteur', petit_mot: 'Petit mot',
};

interface HolderBits { id: string; type: string; text?: string; holder?: string; labelFor?: string }

export function isHolder(el: HolderBits): boolean { return el.type === 'text' && !!el.holder; }

/** True when this element must not be printed (and is shown faded in the editor). */
export function hiddenInPrint(el: HolderBits, all: readonly HolderBits[]): boolean {
  if (isHolder(el)) return !(el.text ?? '').trim();
  if (el.labelFor) {
    const h = all.find((e) => e.id === el.labelFor);
    return !h || !(h.text ?? '').trim();
  }
  return false;
}

/** The elements that print, in order. */
export function printable<T extends HolderBits>(all: readonly T[]): T[] {
  return all.filter((e) => !hiddenInPrint(e, all));
}
